import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PackageCheck } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => `R${(parseFloat(v) || 0).toFixed(2)}`;

export const DirectSaleLineDialog = ({ product, onClose, onAdd }) => {
  const [lines, setLines] = useState([]);
  const [picked, setPicked] = useState(null);
  const [description, setDescription] = useState("");
  const [qty, setQty] = useState("1");
  const [price, setPrice] = useState("");
  const descRef = useRef(null);

  useEffect(() => {
    if (!product) return;
    setPicked(null); setDescription(""); setQty("1"); setPrice(""); setLines([]);
    axios.get(`${API}/direct-lines`, { params: { product_id: product.id } })
      .then((r) => setLines((r.data || []).filter((l) => l.qty_available > 0)))
      .catch(() => {});
    setTimeout(() => descRef.current?.focus(), 100);
  }, [product]);

  if (!product) return null;

  const pick = (l) => {
    setPicked(l);
    setDescription(l.description);
    setQty(String(l.qty_available));
    setPrice(String(l.suggested_price_incl));
  };

  const submit = (e) => {
    e?.preventDefault();
    const q = parseFloat(qty), p = parseFloat(price);
    if (!description.trim()) return toast.error("Enter a description");
    if (!q || q <= 0) return toast.error("Enter a valid quantity");
    if (!p || p <= 0) return toast.error("Enter a selling price");
    if (picked && q > picked.qty_available + 1e-9) return toast.error(`Only ${picked.qty_available} arrived on this line`);
    onAdd({ description: description.trim(), qty: q, price_incl: p, line: picked });
  };

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="direct-sale-line-dialog">
        <DialogHeader><DialogTitle>{product.plu_code} · {product.name}</DialogTitle></DialogHeader>
        {lines.length > 0 && (
          <div className="space-y-1">
            <Label className="text-xs text-gray-500">Arrived, not yet sold: tap to fill</Label>
            <div className="max-h-48 overflow-auto rounded-md border divide-y" data-testid="direct-sale-available-lines">
              {lines.map((l) => (
                <button type="button" key={l.id} onClick={() => pick(l)} data-testid={`direct-sale-line-${l.id}`}
                  className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-gray-50 ${picked?.id === l.id ? "bg-green-50" : ""}`}>
                  <span>
                    <span className="font-medium">{l.description}</span> × {l.qty_available}
                    <span className="block text-xs text-gray-500">{l.grv_number}{l.co_ticket_no ? ` · ${l.co_ticket_no} ${l.co_customer_name || ""}` : ""} · cost {fmt(l.unit_cost_excl)}</span>
                  </span>
                  <span className="whitespace-nowrap font-semibold">{fmt(l.suggested_price_incl)}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        <form onSubmit={submit} className="space-y-3">
          {picked && <div className="flex items-center gap-1 text-xs text-green-700"><PackageCheck className="h-3 w-3" />From {picked.grv_number}, cost {fmt(picked.unit_cost_excl)} excl</div>}
          <div>
            <Label className="text-sm">Description on invoice *</Label>
            <Input ref={descRef} value={description} onChange={(e) => { setDescription(e.target.value); if (picked) setPicked(null); }} placeholder="e.g. Hunter PGP sprinkler" data-testid="direct-sale-description-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Qty *</Label>
              <Input type="number" min="0" step="any" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="direct-sale-qty-input" />
            </div>
            <div>
              <Label className="text-sm">Selling price incl VAT (R) *</Label>
              <Input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="direct-sale-price-input" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="direct-sale-cancel-btn">Cancel</Button>
            <Button type="submit" data-testid="direct-sale-add-btn">Add to Sale</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
